import React from 'react';
import { getEnvironmentVariables } from 'services/environment-variables/environment-variables';
import { ProductVariablesByApp, VariantVariablesByApp } from '../../generated/graphql';
import { Option } from '../../types';
import { formatPrice } from '../../utils';
import { AppAvailability, AppPrice } from './AppTile';

import styles from './AppOptions.module.scss';

type VariablesByApp = ProductVariablesByApp | VariantVariablesByApp;

const SectionHeader: React.FC<{ name: string; withTopLine?: boolean }> = ({ name, withTopLine = false }) => {
  return (
    <div className={`${styles.Section} ${withTopLine ? styles.WithTopLine : null}`}>
      <span>{name.toUpperCase()}</span>
      <div />
    </div>
  );
};

export const sortByBrandAndAppName = (a: VariablesByApp, b: VariablesByApp): number =>
  (a.app.brand?.name ?? '').localeCompare(b.app.brand?.name ?? '') || a.app.name.localeCompare(b.app.name);

type Input = {
  apps: VariablesByApp[];
  hasBrands: boolean;
  venueName?: string;
  menuItemType?: 'Product' | 'MenuVariant';
};
export const getAppPriceAndNameList = ({ apps, hasBrands, venueName, menuItemType }: Input): [Option[], Option[]] => {
  const { AVAILABILITY_BY_VARIANT_WHITELISTED_APP_IDS } = getEnvironmentVariables();

  const whitelistedApps =
    menuItemType === 'MenuVariant' && AVAILABILITY_BY_VARIANT_WHITELISTED_APP_IDS?.length
      ? apps.map((app) => {
          let appId = app.app.id;
          if (appId.startsWith('virtual.')) {
            [, appId] = appId.split('.');
          }
          if (!AVAILABILITY_BY_VARIANT_WHITELISTED_APP_IDS.includes(Number(appId))) {
            return {
              ...app,
              availability: 'unsupported'
            };
          }
          return app;
        })
      : [...apps];

  whitelistedApps.sort(sortByBrandAndAppName);

  const priceListResponse: Option[] = [];
  const appNameResponse: Option[] = [];

  whitelistedApps.forEach((appPrice, index) => {
    const { app, price, availability } = appPrice;
    const isFirstSection = hasBrands && index === 0;
    const isNewSection = hasBrands && index > 0 && whitelistedApps[index - 1]?.app.brand?.name !== app.brand?.name;
    if (isFirstSection || isNewSection) {
      const name = app.brand?.name ?? venueName ?? '';
      const sectionHeader: Option = {
        type: 'text',
        value: name,
        content: <SectionHeader name={name} withTopLine={!isFirstSection} />
      };
      appNameResponse.push(sectionHeader);
      priceListResponse.push(sectionHeader);
    }

    appNameResponse.push({
      content: <AppAvailability app={app} availability={availability} />,
      value: app.id,
      type: 'button'
    });

    priceListResponse.push({
      content: <AppPrice app={app} availability={availability} price={price} />,
      value: `${app.id} - ${formatPrice(price)}`,
      type: 'button'
    });
  });

  return [priceListResponse, appNameResponse];
};
